import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import GroupAccommodationForm from '@/components/forms/GroupAccommodationForm';
import AddToCalendarButton from '@/components/events/AddToCalendarButton';
import { EventPageNotice } from '@/components/events/EventPageNotice';
import { EventWebsiteButton } from '@/components/events/EventWebsiteButton';
import { WithdrawnListingNotice } from '@/components/events/WithdrawnListingNotice';
import YouMightLike from '@/components/recommendations/YouMightLike';
import { BoostShareButton } from '@/components/boost/ShareModal';
import Leaderboard from '@/components/boost/Leaderboard';
import GenomeRadar from '@/components/genome/GenomeRadar';
import BuzzMeter from '@/components/genome/BuzzMeter';
import ROICalculator from '@/components/genome/ROICalculator';
import NetworkingSuggestions from '@/components/genome/NetworkingSuggestions';
import { useTracking } from '@/hooks/useTracking';
import { trackEventView } from '@/components/common/SubscribeModal';
import { Calendar, MapPin, AlertCircle, Loader2, Dna, TrendingUp } from 'lucide-react';
import { getEventWebsiteControl } from '@/utils/externalEventLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to check if event is past
const isEventPast = (event) => {
  if (!event?.dates?.end) return false;
  const endDate = new Date(event.dates.end);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate < today;
};

export default function EventDetailPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { citySlug, venueSlug, eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  
  const [event, setEvent] = useState(null);
  const [city, setCity] = useState(null);
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [withdrawnSlug, setWithdrawnSlug] = useState(null);
  
  // Event Genome Intelligence state
  const [genomeData, setGenomeData] = useState(null);
  const [buzzData, setBuzzData] = useState(null);
  const [genomeLoading, setGenomeLoading] = useState(false);

  // Initialize tracking when event is loaded
  const { trackPageView } = useTracking(event ? {
    eventId: event.id,
    eventSlug: event.slug,
    sectorTags: event.sectorTags,
    organiserName: event.organiserName,
    citySlug: event.citySlug,
    venueSlug: event.venueSlug,
    pageType: 'event_detail'
  } : {});

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/events/${citySlug}/${venueSlug}/${eventSlug}?lang=${lang}`);
        setEvent(response.data.event);
        setCity(response.data.city);
        setVenue(response.data.venue);
        // Track event view for subscribe modal trigger
        trackEventView();
        
        // Track referral click if ref parameter exists
        const refCode = searchParams.get('ref');
        if (refCode) {
          try {
            await axios.get(`${BACKEND_URL}/api/boost/track/${refCode}`);
            // Store ref code for conversion tracking on registration
            sessionStorage.setItem('boost_ref_code', refCode);
          } catch (refError) {
            console.log('Referral tracking:', refError);
          }
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else if (err.response?.status === 410) {
          setWithdrawnSlug(err.response?.data?.detail?.eventSlug || eventSlug);
        } else {
          setError(t('eventDetail.loadError'));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [citySlug, venueSlug, eventSlug, searchParams, lang]);

  const eventWebsiteControl = useMemo(() => (event ? getEventWebsiteControl(event) : null), [event]);

  // Fetch Event Genome Intelligence data
  useEffect(() => {
    const fetchGenomeData = async () => {
      if (!event?.id) return;
      
      setGenomeLoading(true);
      try {
        const [genomeRes, buzzRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/genome/event/${event.id}`).catch(() => null),
          axios.get(`${BACKEND_URL}/api/genome/buzz/${event.id}`).catch(() => null)
        ]);
        
        if (genomeRes?.data?.genome) {
          setGenomeData(genomeRes.data);
        }
        if (buzzRes?.data?.buzz) {
          setBuzzData(buzzRes.data.buzz);
        }
      } catch (err) {
        console.log('Genome data fetch error:', err);
      } finally {
        setGenomeLoading(false);
      }
    };
    
    fetchGenomeData();
  }, [event?.id]);

  if (notFound) {
    return (
      <MainLayout>
        <NotFoundState
          title={t('eventDetail.notFound')}
          primaryTo="/events"
          primaryLabel={t('notFoundPage.eventsCta')}
        />
      </MainLayout>
    );
  }

  if (withdrawnSlug) {
    return (
      <MainLayout>
        <WithdrawnListingNotice slug={withdrawnSlug} />
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">{t('eventDetail.loading')}</span>
        </div>
      </MainLayout>
    );
  }

  if (error || !event) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <p className="text-red-600">{error || t('eventDetail.notFound')}</p>
        </div>
      </MainLayout>
    );
  }

  const isPast = isEventPast(event);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // JSON-LD Event Schema (Enhanced for Google Rich Results)
  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.name,
    "startDate": event.dates?.start,
    "endDate": event.dates?.end,
    "location": {
      "@type": "Place",
      "name": event.venueName,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": event.city,
        "addressCountry": "GB"
      }
    },
    "description": event.shortDescription,
    "organizer": {
      "@type": "Organization",
      "name": event.organiserName,
      "url": event.organiserWebsite
    },
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "eventStatus": isPast ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
    "url": event.officialUrl || `https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}/${eventSlug}`,
    "image": event.imageUrl || "https://events.jigsawconferences.co.uk/og-image.jpg",
    // Offers with price fields (required by Google)
    "offers": {
      "@type": "Offer",
      "url": event.officialUrl || `https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}/${eventSlug}`,
      "price": event.price || "0",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock",
      "validFrom": event.createdAt || event.dates?.start
    },
    // Performer field (can be organization or person)
    "performer": {
      "@type": "Organization",
      "name": event.organiserName || "Various Speakers"
    }
  };

  // E-E-A-T Author Schema for credibility signals
  const authorSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${event.name} - Event Information & Group Accommodation`,
    "author": {
      "@type": "Person",
      "name": "Jigsaw Events Team",
      "jobTitle": "Event Industry Specialists",
      "worksFor": {
        "@type": "Organization",
        "name": "Jigsaw Conferences",
        "url": "https://jigsawconferences.co.uk"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "Jigsaw Events Hub",
      "logo": {
        "@type": "ImageObject",
        "url": "https://events.jigsawconferences.co.uk/logo.png"
      }
    },
    "datePublished": event.createdAt || event.dates?.start,
    "dateModified": event.updatedAt || new Date().toISOString(),
    "mainEntityOfPage": `https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}/${eventSlug}`
  };

  // Speakable schema for voice search
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": event.name,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".event-description", ".event-dates"]
    },
    "url": `https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}/${eventSlug}`
  };

  const isHidden = event.status === 'hidden';

  // Generate SEO-optimized title and description
  const seoTitle = `${event.name} ${new Date(event.dates?.start).getFullYear()} – ${event.city} | Jigsaw Events Hub`;
  const seoDescription = `Join ${event.name} in ${event.city}. ${event.shortDescription?.slice(0, 100)}... Secure free group hotel deals today.`;

  return (
    <MainLayout>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        url={`https://events.jigsawconferences.co.uk/events/${citySlug}/${venueSlug}/${eventSlug}`}
        schema={eventSchema}
      />
      
      {/* Additional E-E-A-T and Speakable Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableSchema) }} />

      {/* Hidden Event Banner */}
      {isHidden && (
        <div className="bg-gray-100 border-b-4 border-gray-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-gray-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-gray-800 text-lg mb-1">{t('eventDetail.hiddenTitle')}</h3>
                <p className="text-gray-600">
                  {t('eventDetail.hiddenDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Past Event Banner */}
      {isPast && !isHidden && (
        <div className="bg-amber-100 border-b-4 border-amber-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-900 text-lg mb-1">{t('eventDetail.pastTitle')}</h3>
                <p className="text-amber-800">
                  {t('eventDetail.pastDesc', { venue: event.venueName, city: event.city })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-orange-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-blue-100 mb-6">
            <LocalizedLink to="/events" className="hover:text-white">{t('eventDetail.breadcrumbEvents')}</LocalizedLink>
            <span>/</span>
            <LocalizedLink to={`/events/${citySlug}`} className="hover:text-white">{city?.name}</LocalizedLink>
            <span>/</span>
            <LocalizedLink to={`/events/${citySlug}/${venueSlug}`} className="hover:text-white">{venue?.name}</LocalizedLink>
          </div>

          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="event-detail-title">
              {event.name}
            </h1>

            <div className="flex flex-col md:flex-row md:items-center md:space-x-8 space-y-3 md:space-y-0 mb-8">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span className="font-medium">{event.dates?.display}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5" />
                <span className="font-medium">{event.venueName}, {event.city}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {(event.sectorTags || []).map((tag) => (
                <Badge key={tag} className="bg-white/20 text-white border-white/40">
                  {tag}
                </Badge>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              {eventWebsiteControl?.publicClickable && (
                <EventWebsiteButton
                  event={event}
                  className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-medium w-full sm:w-auto"
                  buttonTestId="visit-event-website-button"
                />
              )}
              <AddToCalendarButton 
                event={event}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-6 py-6 text-lg rounded-lg font-medium"
              />
              <Button
                onClick={scrollToForm}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg font-medium w-full sm:w-auto"
                data-testid="scroll-to-form-button"
              >
                {t('eventDetail.groupAccomBtn')}
              </Button>
              {!isPast && (
                <BoostShareButton event={event} variant="compact" />
              )}
            </div>
            {!eventWebsiteControl?.publicClickable && eventWebsiteControl?.message && (
              <p className="mt-5 max-w-3xl text-sm leading-6 text-blue-100/90" data-testid="event-website-status-note">
                {eventWebsiteControl.message}
              </p>
            )}
            <EventPageNotice />
          </div>
        </div>
      </section>

      {/* Section 1 - About This Event */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('eventDetail.about', { name: event.name })}</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6">{event.shortDescription}</p>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-6">{event.longDescription}</p>
            <p className="text-gray-900 font-medium border-l-4 border-orange-600 pl-4 py-2 bg-orange-50">
              {t('eventDetail.noTickets')}
            </p>
          </div>
          
          {/* Calendar Subscription Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">📅 {t('eventDetail.subscribeCalendars')}</p>
            {event.venueSlug && (
              <p className="text-sm text-gray-600">
                <a 
                  href={`${BACKEND_URL}/api/public/calendar.ics?venue=${event.venueSlug}`}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                  rel="nofollow noindex"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'calendar_ics_requested', {
                        venue: event.venueSlug,
                        city: event.citySlug,
                        event_name: event.name
                      });
                    } else {
                      console.log('GA Event: calendar_ics_requested', { venue: event.venueSlug });
                    }
                  }}
                >
                  {t('eventDetail.allEventsAtVenue', { venue: event.venueName })}
                </a>
              </p>
            )}
            {event.citySlug && (
              <p className="text-sm text-gray-600">
                <a 
                  href={`${BACKEND_URL}/api/public/calendar.ics?city=${event.citySlug}`}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                  rel="nofollow noindex"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'calendar_ics_requested', {
                        city: event.citySlug,
                        event_name: event.name
                      });
                    } else {
                      console.log('GA Event: calendar_ics_requested', { city: event.citySlug });
                    }
                  }}
                >
                  {t('eventDetail.allEventsInCity', { city: event.city })}
                </a>
              </p>
            )}
            {(event.sectorTags || []).length > 0 && (
              <p className="text-sm text-gray-600">
                <a 
                  href={`${BACKEND_URL}/api/public/calendar.ics?tags=${encodeURIComponent((event.sectorTags || []).join(','))}`}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                  rel="nofollow noindex"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'calendar_ics_requested', {
                        tags: (event.sectorTags || []).join(','),
                        event_name: event.name
                      });
                    } else {
                      console.log('GA Event: calendar_ics_requested', { tags: event.sectorTags });
                    }
                  }}
                >
                  {t('eventDetail.allTagEvents', { tags: (event.sectorTags || []).slice(0, 2).join(' & ') })}
                </a>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Section 2 - Who This Event Is For */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('eventDetail.whoFor')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('eventDetail.whoForDesc', { tags: (event.sectorTags || []).join(', ') })}
          </p>
        </div>
      </section>

      {/* Section 3 - Group Accommodation & Meeting Rooms */}
      <section ref={formRef} className="py-16 bg-white" id="group-enquiry-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('eventDetail.groupAccomTitle', { name: event.name })}</h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            {isPast
              ? t('eventDetail.groupAccomPast', { venue: event.venueName, city: event.city })
              : t('eventDetail.groupAccomFuture', { name: event.name, venue: event.venueName })}
          </p>
          
          <GroupAccommodationForm
            eventDetails={{
              defaultNotes: `Enquiry for ${event.name} at ${event.venueName} in ${event.city}.`,
              hiddenFields: {
                event_name: event.name,
                event_slug: event.slug,
                event_city: event.city,
                event_venue: event.venueName,
                event_official_url: eventWebsiteControl?.url || '',
                source: 'events-hub'
              }
            }}
          />
        </div>
      </section>

      {/* Section 4 - About the Organiser */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('eventDetail.aboutOrganiser')}</h2>
          <p className="text-lg text-gray-700 mb-4">
            <strong>{event.organiserName}</strong> {t('eventDetail.organisesThisEvent')}
          </p>
          <p className="text-gray-600 mb-6">
            {t('eventDetail.programmeDetails')}
          </p>
          <div className="flex flex-wrap gap-4">
            {event.ownerEmail && (
              <a
                href={`${BACKEND_URL}/api/public/calendar.ics?organizerId=${encodeURIComponent(event.ownerEmail)}`}
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                rel="nofollow noindex"
                onClick={() => {
                  if (typeof window !== 'undefined' && window.gtag) {
                    window.gtag('event', 'calendar_ics_requested', {
                      organizerId: event.ownerEmail,
                      event_name: event.name
                    });
                  } else {
                    console.log('GA Event: calendar_ics_requested', { organizerId: event.ownerEmail });
                  }
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {t('eventDetail.subscribeOrganiser', { name: event.organiserName })}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Section 5 - Event Genome Intelligence */}
      {event.id && !isPast && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white" id="event-genome">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <Dna className="w-4 h-4" />
                {t('eventDetail.genomeBadge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('eventDetail.deepAnalysis')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('eventDetail.deepAnalysisDesc')}
              </p>
            </div>

            {/* Genome Cards Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Event DNA Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Dna className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold">{t('eventDetail.eventDna')}</h3>
                      <p className="text-orange-100 text-sm">{t('eventDetail.eventDnaSub')}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  {genomeLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                      <span className="ml-2 text-gray-500">{t('eventDetail.analyzing')}</span>
                    </div>
                  ) : genomeData?.genome ? (
                    <div className="flex flex-col items-center">
                      <GenomeRadar genome={genomeData.genome} size={280} />
                      {genomeData.interpretation && (
                        <div className="mt-4 pt-4 border-t w-full">
                          <p className="text-sm text-gray-600 text-center">
                            {genomeData.interpretation.overall_verdict}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Dna className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>{t('eventDetail.genomeSoon')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Buzz Meter Card */}
              <div>
                {buzzData ? (
                  <BuzzMeter buzzData={buzzData} />
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-orange-500 p-4 text-white">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <TrendingUp className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold">{t('eventDetail.eventBuzz')}</h3>
                          <p className="text-red-100 text-sm">{t('eventDetail.eventBuzzSub')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {genomeLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                          <span className="ml-2 text-gray-500">{t('eventDetail.measuringBuzz')}</span>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <TrendingUp className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p>{t('eventDetail.buzzSoon')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ROI Calculator */}
              <ROICalculator eventId={event.id} eventName={event.name} />

              {/* Networking Matchmaker */}
              <NetworkingSuggestions eventId={event.id} eventName={event.name} />
            </div>
          </div>
        </section>
      )}

      {/* Section 6 - Promoter Leaderboard */}
      {event.id && !isPast && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Leaderboard eventId={event.id} />
          </div>
        </section>
      )}

      {/* Section 7 - Events You Might Like */}
      {event.id && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <YouMightLike 
              currentEventId={event.id}
              variant="inline"
              limit={4}
              source="event_detail"
            />
          </div>
        </section>
      )}
    </MainLayout>
  );
}
